import { useState } from "react";
import { BadgeCheck, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export function LeadVerificationDialog({ open, onOpenChange, eventId }) {
  const [phone, setPhone] = useState("");
  const [participant, setParticipant] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setPhone("");
    setParticipant(null);
    setBusy(false);
  };

  const changeOpen = (nextOpen) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      reset();
    }
  };

  const lookup = async (event) => {
    event.preventDefault();
    setParticipant(null);
    setBusy(true);
    try {
      const response = await api.post("/keeper/lead/lookup", {
        event_id: eventId,
        phone,
      });
      setParticipant(response.data.participant);
    } catch (error) {
      setParticipant(null);
      toast.error(errMsg(error));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!participant) {
      return;
    }
    setBusy(true);
    try {
      const response = await api.post("/keeper/lead/verify", {
        event_id: eventId,
        profile_id: participant.id,
      });
      setParticipant(response.data.participant);
      toast.success(`${response.data.participant.display_name} sudah terverifikasi untuk Lead.`);
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogContent className="max-w-md" data-testid="lead-verification-dialog">
        <DialogHeader>
          <DialogTitle>Verifikasi Peserta Lead</DialogTitle>
          <DialogDescription>
            Cari peserta dengan nomor WhatsApp, lalu konfirmasi kelayakan Lead.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-3" onSubmit={lookup} data-testid="lead-verification-lookup-form">
          <Input
            type="tel"
            value={phone}
            onChange={(event) => {
              setPhone(event.target.value);
              setParticipant(null);
            }}
            placeholder="Nomor WhatsApp peserta"
            required
            data-testid="lead-verification-phone-input"
          />
          <Button
            type="submit"
            variant="outline"
            className="w-full"
            disabled={busy}
            data-testid="lead-verification-lookup-button"
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
            Cari Peserta
          </Button>
        </form>
        {participant && (
          <div className="rounded-lg bg-[#F5F3EB] p-4" data-testid="lead-verification-participant">
            <p className="text-lg font-extrabold text-[#1A3323]">{participant.display_name}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Status Lead: {participant.lead_status === "VERIFIED" ? "Terverifikasi" : "Belum terverifikasi"}
            </p>
            <Button
              className="mt-4 w-full"
              onClick={verify}
              disabled={busy || participant.lead_status === "VERIFIED"}
              data-testid="lead-verification-confirm-button"
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <BadgeCheck className="mr-2 h-4 w-4" />
              {participant.lead_status === "VERIFIED" ? "Sudah Terverifikasi" : "Verifikasi untuk Lead"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}